import { ErrorLike } from '@apollo/client'
import { useCallback, useMemo, useRef, useState } from 'react'

import {
  CollectionItemVariantGroupFragment,
  GetCatalogCollectionQuery,
} from '../../../../../__generated__/graphql/catalog/graphql'
import { useGraphqlClients } from '../../../../../app/_providers/GraphqlClientsProvider.client'
import {
  COLLECTION_ITEM_MAX_ROW_SIZE,
  COLLECTION_ITEM_PAGE_SIZE,
} from '../../../../../brunswick/constants'
import { GET_COLLECTION } from '../../../../../lib/graphql/queries/GET_COLLECTION'
import { LazyQueryStatus } from '../../../../../lib/hooks/types'
import { CollectionItemFilterInput } from '../../../../../types/graphql/collection-view'

type FetchMoreQueryStatus = LazyQueryStatus | 'pending-more'

type CollectionQueryOverview = {
  handle: string
  status: FetchMoreQueryStatus
  data: NonNullable<GetCatalogCollectionQuery['productCatalogCollection']> | null
  error: ErrorLike | undefined | null
}

export type CollectionItemVariantGroup = CollectionItemVariantGroupFragment

type UseCollectionGridSectionCollectionData = {
  filters: NonNullable<
    NonNullable<
      NonNullable<GetCatalogCollectionQuery['productCatalogCollection']>['items']
    >['filters']
  >
  collections: Array<{
    handle: string
    items: NonNullable<NonNullable<GetCatalogCollectionQuery['productCatalogCollection']>['items']>
    numItems: number
  }>
  numItems: number | null
}

export type UseCollectionGridSectionCollectionsProps = {
  collectionHandles: string[]
  filterOptions: CollectionItemFilterInput[]
}

/**
 * Warning, there be dragons. It is tricky to page not only through a collection, but a list of collections.
 * If you are altering this, you should test:
 * - The collection page loads correctly with JavaScript disabled in the browser (SSR looks right, has images and total count)
 * - The collection page can load multiple collections completely through scrolling
 * - Pages of products loaded via scrolling don't dissapear after hovering a tile
 * - All tiles can be hovered to see their options
 * - Scroll location never slips as you scroll and hover
 * - Filters on the page do not show an option as not available, just because it is not available in one collection, but is available in another collection. Basically, all filter options from all collections should show, and only if all collections say an option is unavailable should it be shown as such.
 * - Filters are merged from all collections, without creating duplicates.
 * - Color filters have access to names and swatches
 * - Total item count should be calculatable instantly for all collections, even ones not paged through yet.
 * - Filtering should change the total item count
 * - Filtering for a category only found in the third collection does not cause odd behavior, we skip loading more from the preceeding collections which have zero items
 */
export const useCollectionGridSectionCollections = ({
  collectionHandles,
  filterOptions,
}: UseCollectionGridSectionCollectionsProps) => {
  const { cacheOptimizedClient } = useGraphqlClients()

  // Initialize the array of results for all provided collection handles to query
  const previousBaseCollectionQueries = useRef<CollectionQueryOverview[] | undefined>(undefined)
  const baseCollectionQueries = useMemo(() => {
    return collectionHandles.map((collectionHandle, index) => {
      const cachedResult = cacheOptimizedClient.readQuery({
        query: GET_COLLECTION,
        variables: {
          collectionHandle: collectionHandle,
          nextPageDataOnly: false,
          filters: filterOptions,
          first: index === 0 ? COLLECTION_ITEM_PAGE_SIZE : COLLECTION_ITEM_MAX_ROW_SIZE, // Loading one row helps a lot for the case where we filter to a filter option only found in a later collection than the initial one. That way we see some products right away and not just a page loading indication
        },
      })
      const cachedData = cachedResult?.productCatalogCollection
      const collectionQueryOverview: CollectionQueryOverview = {
        handle: collectionHandle,
        status: cachedData ? 'resolved' : 'pending',
        data: cachedData ?? null,
        error: null,
      }
      return collectionQueryOverview
    })
  }, [cacheOptimizedClient, collectionHandles, filterOptions])
  const [collectionQueries, setCollectionQueries] = useState(baseCollectionQueries)

  // Function for updating the array of query results with new data
  const handleCollectionQueryResponse = useCallback(
    ({
      collectionHandle,
      newQueryResults,
      mode,
    }: {
      collectionHandle: string
      newQueryResults: {
        data: GetCatalogCollectionQuery | undefined
        error?: ErrorLike | undefined
      }
      mode: 'append' | 'replace'
    }) => {
      const currentQueryOverview = collectionQueries.find(
        nextCollectionQuery => nextCollectionQuery.handle === collectionHandle
      )
      if (!currentQueryOverview) return

      if (newQueryResults.error || !newQueryResults.data?.productCatalogCollection) {
        currentQueryOverview.error = newQueryResults.error ?? null
        currentQueryOverview.status = 'rejected'
      } else {
        const newData = newQueryResults.data.productCatalogCollection
        const existingData = currentQueryOverview.data
        currentQueryOverview.status = 'resolved'
        currentQueryOverview.data = {
          ...newData,
          items: {
            ...newData.items,
            filters: newData.items.filters ?? existingData?.items.filters,
            nodes: [
              ...(mode === 'append' ? existingData?.items.nodes ?? [] : []),
              ...newData.items.nodes,
            ],
          },
        }
      }

      setCollectionQueries([...collectionQueries])
    },
    [collectionQueries]
  )

  // This is some React specific magic. Basically, when someone requests new information from this hook,
  // we get a new baseCollectionQueries result. When we have a new baseline, we need to replace our
  // actual state that everything relies on to trigger a series of reactivity. We can't just leverage useMemo,
  // because our query results are not only based on inputs, they also grow via fetchMore. And we can't only
  // leverage useState, because then new inputs for selected filters or collectionHandles wouldn't cause fresh data.
  if (previousBaseCollectionQueries.current !== baseCollectionQueries) {
    // Since we can't use `useQuery` an arbitrary amount of times, we need to trigger queries for all collection handles that did not find data from the Apollo cache that should have been primed on the page in getInitialProps
    baseCollectionQueries.forEach((baseCollectionQuery, index) => {
      if (baseCollectionQuery.data) {
        baseCollectionQuery.status = 'resolved'
        handleCollectionQueryResponse({
          collectionHandle: baseCollectionQuery.handle,
          newQueryResults: {
            data: {
              productCatalogCollection: baseCollectionQuery.data,
            },
          },
          mode: 'replace',
        })
      } else {
        baseCollectionQuery.status = 'pending'
        cacheOptimizedClient
          .query({
            query: GET_COLLECTION,
            variables: {
              collectionHandle: baseCollectionQuery.handle,
              nextPageDataOnly: false,
              filters: filterOptions,
              first: index === 0 ? COLLECTION_ITEM_PAGE_SIZE : COLLECTION_ITEM_MAX_ROW_SIZE, // Loading one row helps a lot for the case where we filter to a filter option only found in a later collection than the initial one. That way we see some products right away and not just a page loading indication
            },
          })
          .then(results => {
            handleCollectionQueryResponse({
              collectionHandle: baseCollectionQuery.handle,
              newQueryResults: results,
              mode: 'replace',
            })
          })
      }
    })
  }
  previousBaseCollectionQueries.current = baseCollectionQueries

  // Reduce/merge down to a single status
  const status = useMemo<FetchMoreQueryStatus>(() => {
    const statuses = collectionQueries.map(collectionQuery => collectionQuery.status)

    return statuses.reduce<FetchMoreQueryStatus>((overallStatus, nextStatus) => {
      if (nextStatus === 'pending' || overallStatus === 'pending') return 'pending'
      if (nextStatus === 'pending-more' || overallStatus === 'pending-more') return 'pending-more'
      if (nextStatus === 'rejected' || overallStatus === 'rejected') return 'rejected'
      if (nextStatus === 'resolved' || overallStatus === 'resolved') return 'resolved'
      return 'idle'
    }, 'idle')
  }, [collectionQueries])

  // Reduce/merge down to a single data output
  const data = useMemo(() => {
    if (status === 'pending') {
      return null
    }

    let hitCollectionWithMoreItemsToLoad = false
    const data = collectionQueries.reduce<UseCollectionGridSectionCollectionData>(
      (overallData, nextCollectionQuery) => {
        // Merge filters
        const nextCollectionFilters = nextCollectionQuery.data?.items.filters
        if (nextCollectionFilters) {
          nextCollectionFilters.forEach(nextFilterReadOnly => {
            const nextFilter = JSON.parse(
              JSON.stringify(nextFilterReadOnly)
            ) as typeof nextFilterReadOnly
            const matchingFilter = overallData.filters.find(
              filter => filter.name === nextFilter.name
            )
            if (!matchingFilter) {
              overallData.filters.push(nextFilter)
            } else {
              nextFilter.values.forEach(nextFilterValue => {
                const matchingFilterValue = matchingFilter.values.find(
                  filterValue => filterValue.value === nextFilterValue.value
                )
                if (!matchingFilterValue) {
                  matchingFilter.values.push(nextFilterValue)
                } else {
                  matchingFilterValue.state =
                    matchingFilterValue.state === 'DISABLED'
                      ? nextFilterValue.state
                      : matchingFilterValue.state
                }
              })
            }
          })
        }

        // Merge numItems
        overallData.numItems =
          (nextCollectionQuery.data?.items.totalCount ?? 0) + (overallData.numItems ?? 0)

        // Merge items
        // Do not merge items if we haven't paged through all prior collections
        if (hitCollectionWithMoreItemsToLoad === false) {
          const nextCollectionItems = nextCollectionQuery.data?.items
          if (nextCollectionItems) {
            overallData.collections.push({
              handle: nextCollectionQuery.handle,
              items: nextCollectionItems,
              numItems: nextCollectionQuery.data?.items.totalCount ?? 0,
            })

            if (nextCollectionItems.totalCount > nextCollectionItems.nodes.length) {
              hitCollectionWithMoreItemsToLoad = true
            }
          }
        }

        return overallData
      },
      {
        filters: [],
        collections: [],
        numItems: 0,
      }
    )

    // Filters come back from the API sorted, however, since we merge multiple collections
    // together, a filter early in the sort order that did not exist in the first collection,
    // but did exist in a later collection, will be at the end of the list. So we need to resort
    // them after they have been merged.
    data.filters.forEach(filter =>
      filter.values.sort((filterValueA, filterValueB) => {
        if (filterValueA.sortOrder === filterValueB.sortOrder) {
          return filterValueA.displayValue.localeCompare(filterValueB.displayValue)
        }
        return filterValueA.sortOrder - filterValueB.sortOrder
      })
    )

    return data
  }, [collectionQueries, status])

  const previousData = useRef(data)
  if (!status.includes('pending')) {
    previousData.current = data
  }

  // Reduce/merge down to a single fetchMore for paging through all collections
  const fetchMore = useMemo(() => {
    if (status === 'pending' || status === 'pending-more' || status === 'rejected') return undefined

    const collectionToQuery = collectionQueries.find(collectionQuery => {
      const hasData = collectionQuery.data
      const hasNextPage = collectionQuery.data?.items.pageInfo.hasNextPage
      const hasItems = (collectionQuery.data?.items.totalCount ?? 0) > 0
      return (hasNextPage && hasItems) || !hasData
    })

    if (collectionToQuery) {
      return async () => {
        collectionToQuery.status = 'pending-more'
        const targetFirstPage =
          (collectionToQuery.data?.items.nodes.length ?? 0) < COLLECTION_ITEM_PAGE_SIZE

        // Set state so we display in a loading state
        setCollectionQueries([...collectionQueries])

        const results = await cacheOptimizedClient.query({
          query: GET_COLLECTION,
          variables: {
            collectionHandle: collectionToQuery.handle,
            nextPageDataOnly: true,
            filters: filterOptions,
            first: COLLECTION_ITEM_PAGE_SIZE,
            after: targetFirstPage ? undefined : collectionToQuery.data?.items.pageInfo.endCursor,
          },
        })

        handleCollectionQueryResponse({
          collectionHandle: collectionToQuery.handle,
          newQueryResults: results,
          mode: targetFirstPage ? 'replace' : 'append',
        })
      }
    } else {
      return undefined
    }
  }, [
    status,
    collectionQueries,
    cacheOptimizedClient,
    filterOptions,
    handleCollectionQueryResponse,
  ])

  return {
    data,
    previousData: previousData.current,
    fetchMore,
    status,
  }
}
